/**
 * Reusable Verification Workspace shell.
 * Employment / Landlord / Documents all render inside this — only the
 * center form (children) differs. Provides:
 *   - Timeline strip at top
 *   - 3-column layout: applicant/claim summary · form (children) · activity+contact log
 *   - Sticky action bar (Save Draft / Awaiting Response / Complete)
 *   - Attempt counter + assignment panel
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import { apiClient, formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  PhoneCall, Save, Send, CheckCircle2, Undo2, User, Clock, AlertTriangle, ClipboardList,
} from "lucide-react";
import ApplicationPackagePanel from "@/components/ApplicationPackagePanel";
import { fmtDate, fmtDateTime } from "@/lib/dateFormat";

const OUTCOME_LABEL = {
  verified: "Verified", partially_verified: "Partially Verified",
  unable_to_verify: "Unable to Verify", discrepancy_identified: "Discrepancy Identified",
};
const OUTCOME_TONE = {
  verified: "bg-emerald-50 text-emerald-800 border-emerald-200",
  partially_verified: "bg-amber-50 text-amber-800 border-amber-200",
  unable_to_verify: "bg-rose-50 text-rose-800 border-rose-200",
  discrepancy_identified: "bg-rose-100 text-rose-900 border-rose-300",
};
const STATUS_LABEL = {
  unassigned: "Unassigned", assigned: "Assigned", in_progress: "In progress",
  awaiting_applicant: "Awaiting applicant", awaiting_employer: "Awaiting employer",
  awaiting_landlord: "Awaiting landlord", awaiting_documents: "Awaiting documents",
  needs_review: "Needs review", completed: "Completed",
};

// Canonical Employment/Landlord verification attempt vocabulary (spec).
// Legacy values are still accepted by the backend for older records.
const ATTEMPT_METHODS = [
  { v: "phone", l: "Phone" },
  { v: "email", l: "Email" },
  { v: "hr_portal", l: "HR Portal" },
  { v: "written", l: "Written" },
  { v: "other", l: "Other" },
];
const ATTEMPT_RESULTS = [
  { v: "no_answer", l: "No Answer" },
  { v: "left_voicemail", l: "Left Voicemail" },
  { v: "email_sent", l: "Email Sent" },
  { v: "waiting_response", l: "Waiting Response" },
  { v: "returned_call", l: "Returned Call" },
  { v: "verification_completed", l: "Verification Completed" },
  { v: "employer_declined", l: "Employer Declined" },
  { v: "invalid_contact_info", l: "Invalid Contact Information" },
  { v: "other", l: "Other" },
];

function ContactAttemptDialog({ track, sid, onLogged, disabled, staff, currentUserId, isSupervisor }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    method: "phone", result: "no_answer",
    contact_person: "", contact_name: "", contact_org: "",
    contact_phone: "", contact_email: "", notes: "",
    other_explanation: "",
    logged_for_staff_id: currentUserId || "",
  });
  const [busy, setBusy] = useState(false);
  const showOther = form.method === "other" || form.result === "other";
  const submit = async () => {
    setBusy(true);
    try {
      const payload = { ...form };
      // Only send override if a supervisor picked a different assignee
      if (!isSupervisor || !payload.logged_for_staff_id || payload.logged_for_staff_id === currentUserId) {
        delete payload.logged_for_staff_id;
      }
      if (!showOther) delete payload.other_explanation;
      await apiClient.post(`/staff-ops/case/${sid}/${track}/contact-attempt`, payload);
      toast.success("Contact attempt logged");
      setOpen(false);
      setForm({ ...form, notes: "", other_explanation: "" });
      onLogged?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Log failed"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="rounded-md w-full" disabled={disabled} data-testid="log-contact-attempt-btn">
          <PhoneCall className="h-4 w-4 mr-2" /> Log verification attempt
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg" data-testid="log-contact-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">Log a verification attempt</DialogTitle>
          <DialogDescription>Every outreach is captured for the permanent case audit trail.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Contact Method</Label>
            <Select value={form.method} onValueChange={(v) => setForm({ ...form, method: v })}>
              <SelectTrigger data-testid="contact-method"><SelectValue /></SelectTrigger>
              <SelectContent>
                {ATTEMPT_METHODS.map((m) => <SelectItem key={m.v} value={m.v}>{m.l}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Outcome</Label>
            <Select value={form.result} onValueChange={(v) => setForm({ ...form, result: v })}>
              <SelectTrigger data-testid="contact-result"><SelectValue /></SelectTrigger>
              <SelectContent>
                {ATTEMPT_RESULTS.map((r) => <SelectItem key={r.v} value={r.v}>{r.l}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-2">
            <Label>Contact Person</Label>
            <Input value={form.contact_person} onChange={(e) => setForm({ ...form, contact_person: e.target.value })}
                   placeholder="Name of person contacted" data-testid="contact-person" />
          </div>
          <div><Label>Phone</Label><Input value={form.contact_phone} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} data-testid="contact-phone" /></div>
          <div><Label>Email</Label><Input value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} data-testid="contact-email" /></div>
          {showOther && (
            <div className="col-span-2">
              <Label>Explain "Other"</Label>
              <Input value={form.other_explanation}
                     onChange={(e) => setForm({ ...form, other_explanation: e.target.value })}
                     placeholder="Free-text explanation for Other" data-testid="contact-other-explanation" />
            </div>
          )}
          <div className="col-span-2"><Label>Notes</Label>
            <Textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="contact-notes" /></div>
          {isSupervisor && (staff || []).length > 0 && (
            <div className="col-span-2">
              <Label>Logged by (supervisor override)</Label>
              <Select value={form.logged_for_staff_id || currentUserId}
                      onValueChange={(v) => setForm({ ...form, logged_for_staff_id: v })}>
                <SelectTrigger data-testid="contact-logged-for"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {(staff || []).map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <div className="text-[11px] text-slate-500 mt-1">Defaults to you. Supervisors may attribute an attempt to another verifier.</div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={submit} disabled={busy} className="bg-[#0A192F] text-white" data-testid="contact-submit">{busy ? "Logging…" : "Log attempt"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AssignmentPanel({ track, sid, verification, staff, onChanged, canEdit, isSupervisor }) {
  const a = verification?.assignment || {};
  const [staffId, setStaffId] = useState(a.staff_id || "");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!staffId) return toast.error("Pick an assignee");
    setBusy(true);
    try {
      // Priority retired 2026-02 — backend derives SLA from the default.
      await apiClient.post(`/staff-ops/case/${sid}/${track}/assign`, { staff_id: staffId });
      toast.success("Assigned");
      onChanged?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Assign failed"); }
    finally { setBusy(false); }
  };
  return (
    <Card className="p-4 bg-white border-slate-200 space-y-3" data-testid="assignment-panel">
      <div className="flex items-center gap-2 mb-1">
        <User className="h-4 w-4 text-slate-500" />
        <div className="ntn-eyebrow text-slate-600">Assignment</div>
      </div>
      <div>
        <Label className="text-xs">Assignee</Label>
        <Select value={staffId} onValueChange={setStaffId} disabled={!canEdit && !isSupervisor}>
          <SelectTrigger data-testid="assign-staff"><SelectValue placeholder="Pick verifier" /></SelectTrigger>
          <SelectContent>
            {(staff || []).map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      {a.due_at && (
        <div className="text-xs text-slate-500 flex items-center gap-1.5">
          <Clock className="h-3.5 w-3.5" /> Due {fmtDateTime(a.due_at)}
        </div>
      )}
      <Button onClick={submit} disabled={busy || (!canEdit && !isSupervisor)} className="w-full bg-[#0A192F] text-white" data-testid="assign-submit">
        {a.staff_id ? "Reassign / update" : "Assign"}
      </Button>
    </Card>
  );
}

function Timeline({ entries }) {
  if (!entries?.length) return <div className="text-xs text-slate-400 italic">No activity yet.</div>;
  return (
    <div className="border border-slate-200 rounded-md divide-y bg-white" data-testid="timeline">
      {entries.slice().reverse().slice(0, 25).map((e) => (
        <div key={e.id} className="px-3 py-2 text-xs">
          <div className="flex items-center gap-2 text-slate-500">
            <ClipboardList className="h-3 w-3" />
            <span className="font-mono">{fmtDateTime(e.at)}</span>
            <span>·</span>
            <span className="font-medium text-slate-700">{e.actor_name}</span>
          </div>
          <div className="text-slate-800 mt-0.5">{e.message}</div>
        </div>
      ))}
    </div>
  );
}

function ContactLog({ entries }) {
  if (!entries?.length) return <div className="text-xs text-slate-400 italic">No contact attempts yet.</div>;
  return (
    <div className="border border-slate-200 rounded-md divide-y bg-white" data-testid="contact-log">
      {entries.slice().reverse().slice(0, 25).map((e) => (
        <div key={e.id} className="px-3 py-2 text-xs">
          <div className="flex items-center gap-2 text-slate-500 mb-0.5">
            <PhoneCall className="h-3 w-3" />
            <span className="font-mono">{fmtDateTime(e.at)}</span>
            <span>·</span>
            <span className="font-medium text-slate-700">{e.staff_name}</span>
          </div>
          <div className="text-slate-800">
            <span className="uppercase text-[10px] font-semibold text-slate-600">{e.method}</span>
            {" · "}
            <span className="capitalize">{e.result?.replace(/_/g, " ")}</span>
            {e.contact_name && <> — {e.contact_name}{e.contact_org && ` (${e.contact_org})`}</>}
          </div>
          {e.notes && <div className="text-slate-600 italic mt-0.5">"{e.notes}"</div>}
        </div>
      ))}
    </div>
  );
}

export default function VerificationWorkspaceShell({
  track, sid, workspace, staff, reload, children,
  onSaveDraft, onComplete, disabledActions,
}) {
  const v = workspace?.verification || {};
  const s = workspace?.screening || {};
  const canEdit = !!workspace?.can_edit;
  const isSupervisor = !!workspace?.supervisor_override_available;
  const currentUserId = workspace?.current_user_id || "";

  const setStatus = async (status) => {
    try {
      await apiClient.post(`/staff-ops/case/${sid}/${track}/status`, { status });
      toast.success(`Status → ${STATUS_LABEL[status]}`);
      reload?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Status change failed"); }
  };

  return (
    <div className="space-y-6" data-testid={`workspace-${track}`}>
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="font-overline text-slate-500 mb-1">Operations · {track === "documents" ? "Document Validation" : `${track[0].toUpperCase()}${track.slice(1)} Verification`}</div>
          <h1 className="ntn-h2 text-slate-900">{s.applicant_name || "—"}</h1>
          <div className="text-sm text-slate-500 mt-1">{s.property_address}</div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Link to={`/screenings/${sid}`} data-testid="back-to-applicant"
                className="inline-flex items-center h-8 px-3 rounded-sm border border-slate-300 bg-white text-xs font-semibold text-slate-700 hover:border-slate-500 hover:text-slate-900 transition-colors">
            ← Back to Applicant
          </Link>
          <Badge className={OUTCOME_TONE[v.outcome] || "bg-slate-100 text-slate-700 border-slate-200"}>
            {v.outcome ? OUTCOME_LABEL[v.outcome] : STATUS_LABEL[v.status]}
          </Badge>
          <Badge variant="outline" data-testid="attempts-badge">
            <PhoneCall className="h-3 w-3 mr-1" /> {v.attempts_count || 0} attempt{(v.attempts_count === 1) ? "" : "s"}
          </Badge>
          {v.assignment?.due_at && (
            <Badge variant="outline"><Clock className="h-3 w-3 mr-1" /> Due {fmtDate(v.assignment.due_at)}</Badge>
          )}
        </div>
      </div>

      {/* Application package — source of truth for this verification */}
      <ApplicationPackagePanel documents={workspace?.claim?.documents || []} />

      {/* Timeline strip */}
      <Card className="p-4 bg-white border-slate-200" data-testid="timeline-card">
        <div className="text-[10px] uppercase tracking-wider text-slate-500 font-medium mb-2">Verification timeline</div>
        <Timeline entries={v.timeline || []} />
      </Card>

      {/* 3-col */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Left: assignment + claim summary */}
        <div className="lg:col-span-3 space-y-4">
          <AssignmentPanel track={track} sid={sid} verification={v} staff={staff}
                           onChanged={reload} canEdit={canEdit} isSupervisor={isSupervisor} />
          <ContactAttemptDialog track={track} sid={sid} onLogged={reload}
                                staff={staff} currentUserId={currentUserId}
                                isSupervisor={isSupervisor}
                                disabled={!canEdit && !isSupervisor} />
          <Card className="p-4 bg-white border-slate-200">
            <div className="ntn-eyebrow text-slate-600 mb-2">Applicant summary</div>
            <div className="text-sm text-slate-800"><span className="text-slate-500">Email:</span> {s.applicant_email || "—"}</div>
            {s.group_id && <div className="text-sm text-slate-500 mt-1">Part of a household</div>}
          </Card>
        </div>

        {/* Center: track-specific form */}
        <div className="lg:col-span-6 space-y-4">
          {children}

          {/* Sticky action bar */}
          <div className="sticky bottom-0 -mx-4 lg:mx-0 p-4 bg-white border-t border-slate-200 rounded-b-md flex flex-wrap gap-2 justify-end shadow-sm">
            <Button variant="outline" onClick={() => onSaveDraft?.()} disabled={disabledActions || (!canEdit && !isSupervisor)}
                    className="rounded-md" data-testid="action-save-draft">
              <Save className="h-4 w-4 mr-2" /> Save draft
            </Button>
            <div className="flex gap-1">
              {["awaiting_applicant", "awaiting_employer", "awaiting_landlord", "awaiting_documents", "needs_review"].map((st) => (
                <Button key={st} variant="ghost" size="sm" onClick={() => setStatus(st)}
                        disabled={!canEdit && !isSupervisor}
                        className="text-xs" data-testid={`action-status-${st}`}>
                  {STATUS_LABEL[st]}
                </Button>
              ))}
            </div>
            <Button onClick={() => onComplete?.()} disabled={disabledActions || (!canEdit && !isSupervisor)}
                    className="rounded-md bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="action-complete">
              <CheckCircle2 className="h-4 w-4 mr-2" /> Complete verification
            </Button>
          </div>
        </div>

        {/* Right: contact log */}
        <div className="lg:col-span-3 space-y-4">
          <Card className="p-4 bg-white border-slate-200" data-testid="contact-log-card">
            <div className="ntn-eyebrow text-slate-600 mb-2">Contact log</div>
            <ContactLog entries={v.contact_log || []} />
          </Card>
        </div>
      </div>

      {!canEdit && !isSupervisor && v.assignment?.staff_id && (
        <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md p-3 flex items-center gap-2">
          <AlertTriangle className="h-4 w-4" />
          This case is locked to {v.assignment.staff_name}. Supervisors can override.
        </div>
      )}
    </div>
  );
}
